/**
 * UI8 PaperShaders Utilities
 * Shared helper for mounting pulsing border shaders.
 * Handles noise texture loading and paper-shaders-ready timing.
 * Depends on window.PaperShaders, which app/views/partials/_shaders.jade
 * imports on demand: requestShaders() asks for it, and `paper-shaders-ready`
 * fires once it is on window.
 *
 * Usage:
 *   window.PaperShadersUtils.mountPulsingBorder(el, params, opacity);
 *
 * params: object of ShaderMount uniforms. Colors can be passed as hex strings:
 *   u_colorBack: '#00000000'          → auto-converted
 *   u_colors: ['#2d68ff', '#a855f7']  → auto-converted
 */

// Some browsers (old devices, GPU-less remote desktops) have no WebGL;
// paper-shaders throws there, so shader decoration is skipped entirely.
function webglSupported() {
  if (webglSupported.cached === undefined) {
    try {
      var canvas = document.createElement('canvas');
      webglSupported.cached = !!(
        window.WebGLRenderingContext &&
        (canvas.getContext('webgl2') || canvas.getContext('webgl'))
      );
    } catch (e) {
      webglSupported.cached = false;
    }
  }
  return webglSupported.cached;
}

// roundRect shipped in Chrome 99/Safari 16; older browsers need a fallback.
if (
  window.CanvasRenderingContext2D &&
  !CanvasRenderingContext2D.prototype.roundRect
) {
  CanvasRenderingContext2D.prototype.roundRect = function (x, y, w, h, r) {
    if (typeof r === 'undefined') r = 0;
    if (typeof r === 'number') r = [r, r, r, r];
    else if (r.length === 1) r = [r[0], r[0], r[0], r[0]];
    else if (r.length === 2) r = [r[0], r[1], r[0], r[1]];
    else if (r.length === 3) r = [r[0], r[1], r[2], r[1]];
    this.moveTo(x + r[0], y);
    this.arcTo(x + w, y, x + w, y + h, r[1]);
    this.arcTo(x + w, y + h, x, y + h, r[2]);
    this.arcTo(x, y + h, x, y, r[3]);
    this.arcTo(x, y, x + w, y, r[0]);
    this.closePath();
    return this;
  };
}

// Kick off the three.js + paper-shaders import (no-op once requested).
function requestShaders() {
  if (window.PaperShaders) return;
  if (window.UI8 && window.UI8.loadShaders) window.UI8.loadShaders();
}

// Request the import when `el` comes within ~600px of the viewport, so a
// border far down the page never pays for it and one in the hero (or the
// search overlay) pays right away.
function requestShadersNear(el) {
  if (window.PaperShaders) return;
  if (!window.IntersectionObserver) return requestShaders();
  var io = new IntersectionObserver(function (entries) {
    if (!entries[0].isIntersecting) return;
    io.disconnect();
    requestShaders();
  }, { rootMargin: '600px' });
  io.observe(el);
}

function mountPulsingBorder(el, params, opacity) {
  if (!el) return;
  if (!webglSupported()) return;
  requestShadersNear(el);

  function mount() {
    var PS = window.PaperShaders;
    var noiseImg = PS.getShaderNoiseTexture();

    function create() {
      var uniforms = Object.assign(
        {
          u_colorBack: '#00000000',
          u_noiseTexture: noiseImg,
          u_fit: 0, u_rotation: 0, u_scale: 1,
          u_offsetX: 0, u_offsetY: 0,
          u_originX: 0.5, u_originY: 0.5,
          u_worldWidth: 0, u_worldHeight: 0,
          u_marginLeft: 0, u_marginRight: 0, u_marginTop: 0, u_marginBottom: 0,
          u_aspectRatio: 0,
        },
        params
      );

      if (typeof uniforms.u_colorBack === 'string') {
        uniforms.u_colorBack = PS.getShaderColorFromString(uniforms.u_colorBack);
      }
      if (Array.isArray(uniforms.u_colors) && typeof uniforms.u_colors[0] === 'string') {
        uniforms.u_colors = uniforms.u_colors.map(PS.getShaderColorFromString);
      }
      uniforms.u_noiseTexture = noiseImg;

      new PS.ShaderMount(
        el,
        PS.pulsingBorderFragmentShader,
        uniforms,
        undefined,
        opacity || 1.0
      );
    }

    if (noiseImg && noiseImg.complete) create();
    else if (noiseImg) noiseImg.onload = create;
    else create();
  }

  if (window.PaperShaders) mount();
  else window.addEventListener('paper-shaders-ready', mount, { once: true });

  // Pause the shader when scrolled out of view, resume when back
  var speed = opacity || 1.0;
  new IntersectionObserver(function (entries) {
    var mount = el.paperShaderMount;
    if (!mount) return;
    if (entries[0].isIntersecting) mount.setSpeed(speed);
    else mount.setSpeed(0);
  }, { rootMargin: '200px' }).observe(el);
}

window.PaperShadersUtils = {
  mountPulsingBorder: mountPulsingBorder,
  webglSupported: webglSupported,
  requestShaders: requestShaders,
  requestShadersNear: requestShadersNear,
};
